import { useState, useEffect, useCallback, useRef } from "react";
import {
  Search,
  Clear as X,
  ExpandMore as ChevronDown,
  CalendarToday as Calendar,
  ArrowForward as ArrowRight,
  MoreVert as MoreVertical,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { getReceiptSubmissions, deleteReceiptSubmision } from "../_request";
import { NoDataPage } from "examples/Widgets/NoDataPage";
import SimpleBackdrop from "components/MDLoader_Backdrop";
import { useAuth } from "context";
import MDButton from "components/MDButton";
import { ViewReceiptDetailsModal } from "./ViewReceiptDetailsModal";

// Helper function to determine permissions
const getReceiptPermissions = (status) => {
  const canEdit = status === "DRAFT";
  const canDelete = status === "WAITING_APPROVAL" || status === "DRAFT";
  return { canEdit, canDelete };
};

const StatusChip = ({ status }) => {
  const getStatusStyles = (status) => {
    switch (status) {
      case "Approved":
        return "bg-green-100 text-green-800 border-green-200";
      case "Rejected":
        return "bg-red-100 text-red-800 border-red-200";
      case "Pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Draft":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusStyles(
        status
      )}`}
    >
      {status}
    </span>
  );
};

StatusChip.propTypes = {
  status: PropTypes.string,
};

const Select = ({ value, onChange, options, placeholder, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 text-sm text-left bg-white border border-gray-300 rounded-md shadow-sm hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-green-100 focus:border-green-600 flex items-center justify-between"
      >
        <span className="text-gray-700">{value || placeholder}</span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${
            isOpen ? "transform rotate-180" : ""
          }`}
        />
      </button>
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="py-1 max-h-60 overflow-auto">
            {options.map((option, index) => (
              <button
                key={index}
                onClick={() => {
                  onChange(option);
                  setIsOpen(false);
                }}
                className="w-full px-3 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

Select.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  className: PropTypes.string,
};

const ActionDropdown = ({
  receipt,
  onEdit,
  onDelete,
  onViewDetails,
  isDeleting,
  canEdit,
  canDelete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Always show dropdown since View Details is always available
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
        disabled={isDeleting}
      >
        <MoreVertical className="text-lg" />
      </button>
      {isOpen && (
        <div className="absolute right-0 z-10 mt-1 w-40 bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="py-1">
            {/* View Details - Always visible */}
            <button
              onClick={() => {
                onViewDetails(receipt);
                setIsOpen(false);
              }}
              className="w-full px-3 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 flex items-center gap-2"
            >
              <VisibilityIcon className="w-4 h-4" />
              View Details
            </button>

            {canEdit && (
              <button
                onClick={() => {
                  onEdit(receipt.id);
                  setIsOpen(false);
                }}
                className="w-full px-3 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 flex items-center gap-2"
              >
                <EditIcon className="w-4 h-4" />
                Edit
              </button>
            )}
            {canDelete && (
              <button
                onClick={() => {
                  onDelete(receipt.id);
                  setIsOpen(false);
                }}
                disabled={isDeleting}
                className="w-full px-3 py-2 text-sm text-left text-red-600 hover:bg-red-50 focus:outline-none focus:bg-red-50 flex items-center gap-2 disabled:opacity-50"
              >
                <DeleteIcon className="w-4 h-4" />
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

ActionDropdown.propTypes = {
  receipt: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func.isRequired,
  isDeleting: PropTypes.bool.isRequired,
  canEdit: PropTypes.bool.isRequired,
  canDelete: PropTypes.bool.isRequired,
};

const OrdersViaReceiptsMobile = ({
  receiptsData,
  loading,
  mapStatus,
  formatDate,
  formatAmount,
  onEdit,
  onDelete,
  onViewDetails,
  deletingId,
}) => {
  const [showMore, setShowMore] = useState({});

  const toggleShowMore = (id) => {
    setShowMore((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return (
      <div className="md:hidden space-y-4">
        <div className="bg-white rounded-lg p-4 shadow">
          <div className="text-center text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  if (receiptsData.length === 0) {
    return (
      <div className="md:hidden space-y-4">
        <div className="bg-white rounded-lg p-4 shadow">
          <NoDataPage />
        </div>
      </div>
    );
  }

  return (
    <div className="md:hidden space-y-4">
      {receiptsData.map((receipt) => {
        const { canEdit, canDelete } = getReceiptPermissions(receipt.status);
        const isDeleting = deletingId === receipt.id;

        return (
          <div key={receipt.id} className="bg-white shadow rounded-lg px-4 py-[10px] space-y-4">
            {/* Header with Order ID */}
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-[#101828]">Order ID</div>
              <span className="text-sm text-[14px]">{receipt.extractedData?.orderID || "-"}</span>
            </div>

            {/* Status with Actions */}
            <div className="flex items-center justify-between border-t border-gray-100 pt-3">
              <div className="text-sm font-medium text-[#101828]">Status</div>
              <div className="flex items-center gap-2">
                <StatusChip status={mapStatus(receipt.status)} />
                <ActionDropdown
                  receipt={receipt}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onViewDetails={onViewDetails}
                  isDeleting={isDeleting}
                  canEdit={canEdit}
                  canDelete={canDelete}
                />
              </div>
            </div>

            {/* Order Amount */}
            <div className="flex border-t border-gray-100 pt-3 items-center justify-between">
              <div className="text-sm font-medium text-[#101828]">Order Amount</div>
              <span className="text-sm text-[14px]">
                {receipt.extractedData?.totalAmountInCents
                  ? formatAmount(receipt.extractedData.totalAmountInCents)
                  : "-"}
              </span>
            </div>

            {/* Restaurant Name */}
            <div className="flex border-t border-gray-100 pt-3 items-center justify-between">
              <div className="text-sm font-medium text-[#101828]">Restaurant</div>
              <span className="text-sm text-[14px]">
                {receipt._restaurant_store_restaurant_store?.name || "-"}
              </span>
            </div>

            {/* Rejection Reason (only show if exists) */}
            {receipt.rejectionReason && (
              <div className="flex border-t border-gray-100 pt-3 items-center justify-between">
                <div className="text-sm font-medium text-[#101828]">Rejection Reason</div>
                <span className="text-sm text-[14px] text-right max-w-[200px]">
                  {receipt.rejectionReason}
                </span>
              </div>
            )}

            {/* Delivery Date */}
            {showMore[receipt.id] && (
              <div className="flex border-t border-gray-100 pt-3 items-center justify-between">
                <div className="text-sm font-medium text-[#101828]">Delivery Date</div>
                <span className="text-sm text-[14px]">
                  {receipt.extractedData?.deliveryDate
                    ? formatDate(receipt.extractedData.deliveryDate)
                    : "-"}
                </span>
              </div>
            )}

            {/* See more link */}
            <div className="pt-4 border-t border-gray-100 w-full flex items-center justify-center">
              <button
                onClick={() => toggleShowMore(receipt.id)}
                className="text-gray-400 font-medium text-sm hover:text-gray-600 transition-colors"
              >
                {showMore[receipt.id] ? "See less" : "See more"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

OrdersViaReceiptsMobile.propTypes = {
  receiptsData: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  mapStatus: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired,
  formatAmount: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func.isRequired,
  deletingId: PropTypes.string,
};

export default function OrdersViaReceipts() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [searchValue, setSearchValue] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [receiptsData, setReceiptsData] = useState([]);
  const { showSnackbarWithMessage } = useAuth();

  // Modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedReceiptData, setSelectedReceiptData] = useState(null);

  const [pagination, setPagination] = useState({
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
  });
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [receiptToDelete, setReceiptToDelete] = useState(null);

  const statusOptions = ["All Statuses", "Approved", "Rejected", "Pending", "Draft"];

  // Function to map display status to API status
  const mapStatusToAPI = (displayStatus) => {
    switch (displayStatus) {
      case "Pending":
        return "WAITING_APPROVAL";
      case "Rejected":
        return "REJECTED";
      case "Approved":
        return "APPROVED";
      case "Draft":
        return "DRAFT";
      default:
        return undefined;
    }
  };

  // Function to map API status to display status
  const mapStatus = (apiStatus) => {
    switch (apiStatus) {
      case "WAITING_APPROVAL":
        return "Pending";
      case "REJECTED":
        return "Rejected";
      case "APPROVED":
        return "Approved";
      case "DRAFT":
        return "Draft";
      default:
        return apiStatus;
    }
  };

  // Function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Function to format amount
  const formatAmount = (amountInCents) => {
    return `$${(amountInCents / 100).toFixed(2)}`;
  };

  // Function to parse date range
  const parseDateRange = (dateRangeString) => {
    if (!dateRangeString || !dateRangeString.includes("–")) {
      return { startDate: undefined, endDate: undefined };
    }

    const [startStr, endStr] = dateRangeString.split("–").map((s) => s.trim());

    try {
      const startDate = new Date(startStr).toISOString();
      const endDate = new Date(endStr).toISOString();
      return { startDate, endDate };
    } catch (error) {
      return { startDate: undefined, endDate: undefined };
    }
  };

  // Function to fetch receipts data
  const fetchReceiptsData = useCallback(
    async (page = 1, filters = {}) => {
      try {
        setLoading(true);
        const { startDate, endDate } = parseDateRange(filters.dateRange || dateRange);
        const params = {
          page,
          pageSize: 10,
          status:
            filters.statusFilter !== "All Statuses"
              ? mapStatusToAPI(filters.statusFilter)
              : undefined,
          startDate,
          endDate,
        };

        // Remove undefined values
        Object.keys(params).forEach((key) => params[key] === undefined && delete params[key]);

        const response = await getReceiptSubmissions(params);

        if (response.data.success) {
          let filteredData = response.data.data.records;

          // Apply client-side search filter if search value exists
          const searchTerm = filters.searchValue || searchValue;
          if (searchTerm && searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            filteredData = filteredData.filter((receipt) => {
              const restaurantName =
                receipt._restaurant_store_restaurant_store?.name?.toLowerCase() || "";
              const orderID = receipt.extractedData?.orderID?.toLowerCase() || "";
              const rejectionReason = receipt.rejectionReason?.toLowerCase() || "";

              return (
                restaurantName.includes(searchLower) ||
                orderID.includes(searchLower) ||
                rejectionReason.includes(searchLower)
              );
            });
          }

          setReceiptsData(filteredData);
          setPagination(response.data.data.pagination);
        }
      } catch (error) {
        console.error("Error fetching receipts:", error);
        setReceiptsData([]);
      } finally {
        setLoading(false);
      }
    },
    [dateRange, searchValue]
  );

  // Handle view details action
  const handleViewDetails = (receipt) => {
    setSelectedReceiptData(receipt);
    setViewModalOpen(true);
  };

  // Handle edit action
  const handleEdit = (receiptId) => {
    navigate(`/upload-receipts?${receiptId}`);
  };

  // Handle delete action
  const handleDelete = (receiptId) => {
    setReceiptToDelete(receiptId);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!receiptToDelete) return;
    try {
      setDeletingId(receiptToDelete);
      const {
        data: { success, message },
      } = await deleteReceiptSubmision(receiptToDelete);
      if (success) {
        // Refresh the data
        showSnackbarWithMessage({
          type: "success",
          message: "Receipt Deleted Successfully",
        });
        fetchReceiptsData(pagination.currentPage, { statusFilter, searchValue, dateRange });

        setDeleteDialogOpen(false);
        setReceiptToDelete(null);
      } else {
        showSnackbarWithMessage({
          type: "error",
          message,
        });
      }
    } catch (error) {
      console.error("Error deleting receipt:", error);
      showSnackbarWithMessage({
        type: "error",
        message: error.response?.data?.message || "Failed to delete receipt submission",
      });
    } finally {
      setDeletingId(null);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setReceiptToDelete(null);
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchReceiptsData(1);
  }, []);

  // Handle status filter change
  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchReceiptsData(1, { statusFilter: newStatus, searchValue, dateRange });
  };

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchValue !== undefined) {
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
        fetchReceiptsData(1, { statusFilter, searchValue, dateRange });
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchValue, fetchReceiptsData, statusFilter, dateRange]);

  // Handle date range change
  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchReceiptsData(1, { statusFilter, searchValue, dateRange: newDateRange });
  };

  // Handle pagination change
  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
    fetchReceiptsData(newPage, { statusFilter, searchValue, dateRange });
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setStatusFilter("All Statuses");
    setSearchValue("");
    setDateRange("");
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchReceiptsData(1, { statusFilter: "All Statuses", searchValue: "", dateRange: "" });
  };

  // Handle search button click
  const handleSearchClick = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchReceiptsData(1, { statusFilter, searchValue, dateRange });
  };

  return (
    <div className="w-full mt-6 mx-auto md:p-6 md:rounded-xl md:bg-white">
      {/* Header */}
      {statusFilter !== "All Statuses" && (
        <div className="md:flex items-center gap-4 mb-6 hidden">
          <span className="text-sm font-medium text-[#475467] border-gray-400">
            {pagination.totalCount} Results found
          </span>
          <button
            onClick={handleClearFilters}
            className="flex items-center text-[#99A1B7] border-l pl-4 gap-1 text-sm hover:text-gray-500 transition-colors"
          >
            <CancelIcon className="w-4 h-4" />
            <span className="mt-[1px]">Clear</span>
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <Select
          value={statusFilter}
          onChange={handleStatusFilterChange}
          options={statusOptions}
          placeholder="All Statuses"
          className="w-full md:w-[150px]"
        />
      </div>

      {/* Desktop Table - Hidden on mobile */}
      <div className="hidden md:block bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto min-h-[200px]">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#717680]">
                  Restaurant Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#717680]">
                  Order ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#717680]">
                  Delivery Date
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-[#717680]">
                  Reason for Rejection
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#717680]">
                  Order Amount
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-[#717680]">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-[#717680]">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    <SimpleBackdrop />
                  </td>
                </tr>
              ) : receiptsData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    <NoDataPage />
                  </td>
                </tr>
              ) : (
                receiptsData.map((receipt) => {
                  const { canEdit, canDelete } = getReceiptPermissions(receipt.status);

                  return (
                    <tr key={receipt.id} className="hover:bg-gray-50/60 transition-colors">
                      <td className="px-4 py-3 text-sm text-[#535862]">
                        {receipt._restaurant_store_restaurant_store?.name || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm text-[#181D27] font-medium">
                        <div className="flex items-center gap-2">
                          <span className="text-center">
                            {receipt.extractedData?.orderID || "-"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-left text-[#535862]">
                        {receipt.extractedData?.deliveryDate
                          ? formatDate(receipt.extractedData.deliveryDate)
                          : "-"}
                      </td>
                      <td className="px-4 py-3 text-sm text-[#181D27] font-medium text-center">
                        {receipt.rejectionReason || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm text-left text-[#535862]">
                        {receipt.extractedData?.totalAmountInCents
                          ? formatAmount(receipt.extractedData.totalAmountInCents)
                          : formatAmount(receipt.extractedData.subTotalInCents)}
                      </td>
                      <td className="px-4 py-3 text-sm text-center">
                        <StatusChip status={mapStatus(receipt.status)} />
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <ActionDropdown
                          receipt={receipt}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          onViewDetails={handleViewDetails}
                          isDeleting={deletingId === receipt.id}
                          canEdit={canEdit}
                          canDelete={canDelete}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards - Shown only on mobile */}
      <OrdersViaReceiptsMobile
        receiptsData={receiptsData}
        loading={loading}
        mapStatus={mapStatus}
        formatDate={formatDate}
        formatAmount={formatAmount}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onViewDetails={handleViewDetails}
        deletingId={deletingId}
      />

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center border-t border-gray-200 py-4 mt-6">
          <Pagination
            count={pagination.totalPages}
            page={pagination.currentPage}
            onChange={handlePageChange}
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        maxWidth={false}
        PaperProps={{
          sx: {
            maxWidth: 420,
            margin: "auto",
          },
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this receipt submission? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <MDButton
            fullWidth
            variant="outlined"
            onClick={cancelDelete}
            color="black"
            sx={{
              borderRadius: "8px",
              padding: "10px",
              textTransform: "none",
              border: "1px solid #D0D5DD",
              fontSize: 14,
              boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
            }}
          >
            Cancel
          </MDButton>

          <MDButton
            onClick={confirmDelete}
            color="error"
            variant="contained"
            fullWidth
            disabled={deletingId !== null}
          >
            {deletingId ? "Deleting..." : "Delete"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* View Receipt Details Modal */}
      <ViewReceiptDetailsModal
        showModal={viewModalOpen}
        setShowModal={setViewModalOpen}
        receiptData={selectedReceiptData}
      />
    </div>
  );
}
